/**
 * 路由栈管理工具 - 简化版
 *
 * 功能：
 * 1. 记录路由历史栈
 * 2. 返回时以完整路径为单位跳转
 * 3. 在栈底（最后一个路由）时，由调用方判断是否显示退出确认
 */

export interface RouteStackManager {
  push: (path: string, replace?: boolean) => void;
  pop: () => string | null;
  canPop: () => boolean;
  isAtBottom: () => boolean;
  getStack: () => string[];
  clear: () => void;
  insertBeforeCurrent: (path: string) => void;
}

class RouteStack implements RouteStackManager {
  private stack: string[] = [];

  /**
   * 压入新路径到栈中
   * @param path - 路由路径
   * @param replace - 是否替换栈顶元素（用于频道页切换）
   */
  push(path: string, replace = false) {
    // 如果路径与栈顶相同，不重复添加
    if (this.stack.length > 0 && this.stack[this.stack.length - 1] === path) {
      return;
    }

    // 如果是替换模式且栈不为空，替换栈顶元素
    if (replace && this.stack.length > 0) {
      this.stack[this.stack.length - 1] = path;
      console.log(`[RouteStack] 替换栈顶: ${path}, 栈深度: ${this.stack.length}`);
      return;
    }

    // 添加路径到栈顶
    this.stack.push(path);
    console.log(`[RouteStack] 压入路径: ${path}, 栈深度: ${this.stack.length}`);

    // 限制栈深度，保留最近 50 条记录
    if (this.stack.length > 50) {
      this.stack = this.stack.slice(-50);
    }
  }

  /**
   * 从栈中弹出当前路径，返回上一个路径
   * @returns 上一个路径，如果栈中只剩一个元素则返回 null
   */
  pop(): string | null {
    // 如果栈为空或只有一个元素，返回 null
    if (this.stack.length <= 1) {
      return null;
    }

    // 移除栈顶（当前路径）
    this.stack.pop();

    // 返回新的栈顶（上一个路径）
    const previousPath = this.stack[this.stack.length - 1];
    return previousPath;
  }

  /**
   * 检查是否可以返回
   * @returns 如果栈中只剩一个元素则返回 false
   */
  canPop(): boolean {
    return this.stack.length > 1;
  }

  /**
   * 检查是否在栈底（只剩最后一个路由）
   * @returns 如果栈中只有一个元素则返回 true
   */
  isAtBottom(): boolean {
    return this.stack.length <= 1;
  }

  /**
   * 获取当前栈的副本
   */
  getStack(): string[] {
    return [...this.stack];
  }

  /**
   * 清空栈
   */
  clear() {
    this.stack = [];
  }

  /**
   * 在当前路径(栈顶)之前插入一个新路径
   * @param path - 要插入的路由路径
   */
  insertBeforeCurrent(path: string) {
    if (this.stack.length === 0) {
      // 如果栈为空,直接push
      this.push(path);
      return;
    }

    // 取出当前路径(栈顶)
    const currentPath = this.stack.pop();

    // 插入新路径
    this.stack.push(path);

    // 将当前路径重新放回栈顶
    if (currentPath) {
      this.stack.push(currentPath);
    }

    console.log(`[RouteStack] 在当前路径前插入: ${path}, 栈深度: ${this.stack.length}`);
  }
}

// 导出单例实例
export const routeStackManager = new RouteStack();
